import React from "react";
import PropTypes from "prop-types";
import { HexColorPicker } from "react-colorful";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { EDITOR_ACCENT } from "../editorUiConstants";
import { EDITOR_COLOR_SWATCHES } from "../utils/editorColorPalette";

const HEX_PATTERN = /^#([0-9a-fA-F]{6})$/;

export const normalizePickerHex = (value, fallback = "#000000") => {
  if (!value) {
    return fallback;
  }
  const trimmed = String(value).trim();
  if (HEX_PATTERN.test(trimmed)) {
    return trimmed.toLowerCase();
  }
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) {
    return `#${trimmed.toLowerCase()}`;
  }
  return fallback;
};

const defaultPickerStyles = {
  "& .react-colorful": { width: 220, height: 160 },
  "& .react-colorful__saturation": { borderRadius: "8px 8px 0 0" },
  "& .react-colorful__hue": { height: 12, borderRadius: "0 0 8px 8px", mt: 1 },
  "& .react-colorful__pointer": { width: 18, height: 18 },
};

const compactPickerStyles = {
  "& .react-colorful": { width: 168, height: 112 },
  "& .react-colorful__saturation": { borderRadius: "6px 6px 0 0" },
  "& .react-colorful__hue": { height: 10, borderRadius: "0 0 6px 6px", mt: 0.75 },
  "& .react-colorful__pointer": { width: 14, height: 14 },
};

const ColorPickerPopoverContent = ({
  color,
  compact = false,
  defaultColor,
  onChange,
  onApply,
  onSwatchSelect,
  showHexInput = false,
  swatches = EDITOR_COLOR_SWATCHES,
}) => {
  const pickerStyles = compact ? compactPickerStyles : defaultPickerStyles;
  const swatchSize = compact ? 18 : 24;
  const swatchGap = compact ? 0.5 : 0.75;

  const handleHexInput = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      return;
    }
    if (HEX_PATTERN.test(trimmed) || /^[0-9a-fA-F]{6}$/.test(trimmed)) {
      onChange(normalizePickerHex(trimmed, color));
    }
  };

  return (
    <>
      <Box sx={pickerStyles}>
        <HexColorPicker color={color} onChange={onChange} />
      </Box>

      {showHexInput ? (
        <TextField
          size="small"
          fullWidth
          value={color}
          placeholder="#000000"
          onChange={(event) => handleHexInput(event.target.value)}
          inputProps={{ spellCheck: false, "aria-label": "Codice colore hex" }}
          sx={{
            mt: compact ? 0.75 : 1,
            "& .MuiInputBase-root": {
              height: compact ? 28 : 32,
              fontSize: compact ? "0.75rem" : "0.8125rem",
            },
          }}
        />
      ) : null}

      <Stack
        direction="row"
        flexWrap="wrap"
        gap={swatchGap}
        sx={{ mt: compact ? 0.75 : 1.25, maxWidth: compact ? 168 : 220 }}
      >
        {swatches.map((swatch) => (
          <Box
            key={swatch}
            component="button"
            type="button"
            aria-label={`Usa colore ${swatch}`}
            onClick={() => (onSwatchSelect || onApply || onChange)(swatch)}
            sx={{
              width: swatchSize,
              height: swatchSize,
              p: 0,
              border: color === swatch ? `2px solid ${EDITOR_ACCENT}` : "1px solid #cbd5e1",
              borderRadius: 0.75,
              backgroundColor: swatch,
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
        ))}
      </Stack>

      {!showHexInput ? (
        <Typography variant="caption" sx={{ display: "block", mt: 1, color: EDITOR_ACCENT, fontWeight: 600 }}>
          {color}
        </Typography>
      ) : null}

      <Stack direction="row" spacing={0.5} sx={{ mt: compact ? 0.75 : 1.25 }}>
        {defaultColor ? (
          <Button
            type="button"
            size="small"
            variant="outlined"
            onClick={() => (onSwatchSelect || onApply || onChange)(defaultColor)}
            sx={{
              minWidth: 0,
              px: compact ? 0.75 : 1,
              py: compact ? 0.25 : 0.5,
              fontSize: compact ? "0.6875rem" : "0.75rem",
              textTransform: "none",
              borderColor: "#cbd5e1",
              color: "#475569",
            }}
          >
            Predefinito
          </Button>
        ) : null}
        {onApply ? (
          <Button
            type="button"
            size="small"
            variant="contained"
            onClick={() => onApply(color)}
            sx={{
              minWidth: 0,
              px: compact ? 1 : 1.25,
              py: compact ? 0.25 : 0.5,
              fontSize: compact ? "0.6875rem" : "0.75rem",
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Applica
          </Button>
        ) : null}
      </Stack>
    </>
  );
};

ColorPickerPopoverContent.propTypes = {
  color: PropTypes.string.isRequired,
  compact: PropTypes.bool,
  defaultColor: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onApply: PropTypes.func,
  onSwatchSelect: PropTypes.func,
  showHexInput: PropTypes.bool,
  swatches: PropTypes.arrayOf(PropTypes.string),
};

export default ColorPickerPopoverContent;
